import type { SelectChangeEvent, SelectProps } from "@mui/material"
import {
  Checkbox,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
} from "@mui/material"
import { KeyboardArrowDownOutlined } from "@mui-symbols-material/w300"
import { type ReactElement, forwardRef, useId } from "react"

import { type AerosTagData, AerosTagStack } from "@/components/UiKit"
import { MENU_PROPS } from "@/constants/forms"
import type { Option } from "@/types/form-fields"

export type AerosMultiSelectProps<T extends string | number = string> = Omit<
  SelectProps<T[]>,
  "onChange" | "multiple" | "renderValue"
> & {
  options: Option<T>[]
  label?: string
  placeholder?: string
  helperText?: string
  required?: boolean
  withSelectAll?: boolean
  getTagLabel?: (option: Option<T>) => string
  onChange?: (value: T[]) => void
  "data-testid"?: string
}

export const AerosMultiSelect = forwardRef(
  <T extends string | number = string>(
    {
      options,
      label,
      placeholder = "Select options",
      helperText,
      required,
      error,
      withSelectAll = false,
      getTagLabel = (option) => option.label,
      onChange,
      MenuProps,
      className,
      "data-testid": testId,
      ...props
    }: AerosMultiSelectProps<T>,
    ref: React.Ref<HTMLDivElement>
  ): ReactElement => {
    const labelId = useId()
    const selectedValues = (props.value || []) as T[]
    const enabledValues = options.filter((option) => !option.disabled).map((option) => option.value)

    const isAllSelected = selectedValues.length === enabledValues.length && enabledValues.length > 0
    const isIndeterminate = selectedValues.length > 0 && selectedValues.length < enabledValues.length

    const handleChange = (e: SelectChangeEvent<T[]>) => {
      const values = e.target.value as T[]
      onChange?.(values)
    }

    const handleSelectAll = () => {
      onChange?.(isAllSelected ? [] : enabledValues)
    }

    const handleTagDelete = (tagToDelete: AerosTagData) => {
      const values = selectedValues.filter((value: T) => value !== tagToDelete.value)
      onChange?.(values)
      if (!values.length) {
        // When the last chip is removed its DOM node is deleted before blur fires,
        // leaving the Select in a stuck focused state. Blur explicitly to clear it.
        ;(document.activeElement as HTMLElement | null)?.blur()
      }
    }

    const tags: AerosTagData[] = selectedValues.map((value) => {
      const selectedOption = options.find((option) => option.value === value)
      return {
        key: value,
        label: selectedOption ? getTagLabel(selectedOption) : String(value),
        value,
      }
    })

    return (
      <FormControl ref={ref} className={className} fullWidth required={required} error={error} data-testid={testId}>
        {label && <InputLabel id={labelId}>{label}</InputLabel>}
        <Select
          {...props}
          value={selectedValues}
          labelId={label && labelId}
          variant='outlined'
          multiple
          displayEmpty
          IconComponent={KeyboardArrowDownOutlined}
          MenuProps={MenuProps ?? MENU_PROPS}
          onChange={handleChange}
          renderValue={(selected) => {
            if (!selected.length) {
              return <span className='text-gray-500'>{placeholder}</span>
            }
            return <AerosTagStack tags={tags} onDelete={handleTagDelete} disabled={props.disabled} />
          }}
        >
          {withSelectAll && enabledValues.length > 0 && (
            <>
              <MenuItem onClick={handleSelectAll}>
                <Checkbox checked={isAllSelected} indeterminate={isIndeterminate} />
                <ListItemText primary='Select All' />
              </MenuItem>
              <Divider className='!my-0' />
            </>
          )}
          {options.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              className={withSelectAll ? "pl-8" : undefined}
            >
              <Checkbox checked={selectedValues.includes(option.value)} />
              <ListItemText primary={option.customLabel ?? option.label} />
            </MenuItem>
          ))}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    )
  }
) as <T extends string | number = string>(
  props: AerosMultiSelectProps<T> & { ref?: React.Ref<unknown> }
) => ReactElement
